import { Color3 } from '@babylonjs/core/Maths/math.color';
import { DynamicTexture } from '@babylonjs/core/Materials/Textures/dynamicTexture';
import type { StandardMaterial } from '@babylonjs/core/Materials/standardMaterial';
import type { Scene } from '@babylonjs/core/scene';

import { as2d } from '../core/canvas2d';
import { BAND_COUNT, type VoiceBandsSample } from '../voice/VoiceBands';
import { MIC_QUIET_RMS } from '../voice/VoiceActivity';

/**
 * Draws a peer's live voice signal onto the visor — the visible half of the two-part change this
 * pull request is; it only exists because `AvatarManager.ts` first stopped collapsing the avatar's
 * two authored material slots into one flat colour (`ModelLibrary.ts`'s `preserveMaterials`
 * option, and `applyAvatarMaterial` in `AvatarManager.ts`), which is what makes the visor a
 * distinct, individually-recolourable surface at all rather than part of one head-wide tint.
 *
 * One `VisorVisualizer` per peer, owning one small `DynamicTexture` assigned as the
 * `diffuseTexture` of that peer's own cloned `Mat.Avatar.Tint` sub-material — never the shared
 * template's, never another peer's; `AvatarManager.applyAvatarMaterial` is what guarantees this
 * material is this peer's alone. Colour and bars are one texture, deliberately: the peer's own
 * presence colour fills the whole canvas first, then bars are drawn over it in white, so every
 * one of the three states below is purely a canvas draw with no material juggling — nothing here
 * ever swaps `diffuseColor` or reaches for a second material.
 *
 * **Three states, derived entirely from what `PeerVoicePlayer`/`VoiceBands` already track — no
 * new hub method, no new wire field:**
 *  - no stream for this peer (`hasStream: false`) → microphone off → bars at 10% white opacity,
 *    flat at a resting baseline;
 *  - a stream, but RMS below `MIC_QUIET_RMS` → microphone on, quiet → bars full white, still
 *    flat at the same baseline;
 *  - RMS at or above that threshold → speaking → bars full white, animated from the live
 *    12-band spectrum `VoiceBands` supplies.
 *
 * That threshold is imported, not redefined: `voice/VoiceActivity.ts` is the one place this
 * project decides what "quiet" means for a microphone signal, and a second, independently-tuned
 * threshold here would let an avatar look silent while some other reader of the same signal
 * still thought the person was talking (CLAUDE.md's own running theme of "one definition, not
 * two" — MessagePack casing, the model catalogue).
 *
 * **This module knows how to draw a bar; it does not know what a voice sounds like.** Everything
 * upstream of `draw(sample, hasStream)` — the `AnalyserNode`, the FFT, the RMS, the 15Hz tick —
 * lives in `voice/VoiceBands.ts`, whose own `VoiceBandsSample` (`{ bands, rms }`) is the entire
 * interface between the two. A later move to, say, a shader-driven visor changes this file alone.
 */

/** The visor's own texture resolution — small on purpose; this renders at a few centimetres
 *  across on a headset, not on a monitor. 64, not 32: a resolution change only, made alongside
 *  lowering `MAX_BAR_HEIGHT_RATIO` below — at the new 0.5 cap the resting nubs
 *  (`BASELINE_BAND_LEVEL * MAX_BAR_HEIGHT_RATIO * TEXTURE_HEIGHT`) work out to 0.12 * 0.5 * 32 ≈
 *  1.9px tall at the old resolution, which reads as chunky; doubling to 64 gives them ~3.8px.
 *  Every dimension in `drawMirroredBars` is a fraction of `TEXTURE_WIDTH`/`TEXTURE_HEIGHT`, so the
 *  layout in UV space is unchanged by this — only sampling resolution changes, not the aspect. The
 *  aspect itself (4:1, texture, versus the visor's own physical 176x82mm ≈ 2.15:1) is deliberately
 *  left alone: it makes a bar read about 1.9x taller relative to its width on the visor than a flat
 *  preview of the texture suggests, which is toward the intended thin-vertical-bar look, not away
 *  from it, so there is nothing here to "fix". */
const TEXTURE_WIDTH = 128;
const TEXTURE_HEIGHT = 64;

/**
 * The owner's own change, not a tuning knob, after seeing a reference image: the waveform must
 * cut across the middle of the visor at no more than half its height. Previously 0.7; the owner
 * lowered it to 0.5, with the same force the 0.7 value itself carried before — the constraint is
 * "bars must never fill the visor, or it stops reading as a face," and 0.5 is where the owner has
 * now drawn that line. 0.5 leaves a visible margin — (1 - 0.5) / 2 of the texture height — above
 * and below the tallest possible bar, centred vertically, regardless of how loud a peer gets.
 */
const MAX_BAR_HEIGHT_RATIO = 0.5;

/** The flat, non-animated bar height used for both the "off" and "idle" states — a resting nub,
 *  not zero: a zero-height bar would make the 10%-vs-100% opacity distinction between those two
 *  states invisible, since nothing would be drawn either way. */
const BASELINE_BAND_LEVEL = 0.12;

const OFF_BAR_ALPHA = 0.1;
const ON_BAR_ALPHA = 1;

export type VisorState = 'off' | 'idle' | 'speaking';

/**
 * The three-state selection itself, pure and exported so it can be proven directly — no
 * `AudioContext`, no canvas, just the rule the module doc states: no stream is `off` regardless
 * of `rms`; a stream is `idle` below `threshold` and `speaking` at or above it.
 */
export function selectVisorState(hasStream: boolean, rms: number, threshold: number): VisorState {
  if (!hasStream) {
    return 'off';
  }

  return rms >= threshold ? 'speaking' : 'idle';
}

/** The narrow slice of `CanvasRenderingContext2D` bar-drawing actually needs — factored out so
 *  `drawMirroredBars` is testable with a plain recorder, no real canvas involved. */
export interface FillRectTarget {
  fillRect(x: number, y: number, w: number, h: number): void;
}

/**
 * Draws `BAND_COUNT` levels (0..1 each) as `BAND_COUNT * 2` bars, mirrored left and right around
 * the texture's own vertical centre — band 0 innermost (nearest the centre line on both sides),
 * increasing outward. Every bar is centred vertically and capped at `MAX_BAR_HEIGHT_RATIO` of
 * `TEXTURE_HEIGHT`, regardless of the input level, which is the hard constraint above — expressed
 * here, not merely described, since this is the one function that actually turns a number into a
 * height.
 *
 * `context.fillStyle` is the caller's responsibility (`VisorVisualizer.draw`, which sets it once
 * for the whole bar pass) — this only ever calls `fillRect`.
 */
export function drawMirroredBars(context: FillRectTarget, levels: readonly number[]): void {
  const halfWidth = TEXTURE_WIDTH / 2;
  const slotWidth = halfWidth / BAND_COUNT;
  const barWidth = Math.max(1, slotWidth - 1);

  levels.forEach((rawLevel, band) => {
    const level = Math.min(1, Math.max(0, rawLevel));
    const height = level * MAX_BAR_HEIGHT_RATIO * TEXTURE_HEIGHT;
    const y = (TEXTURE_HEIGHT - height) / 2;

    // Band 0 sits adjacent to the centre line on both sides; higher bands step outward.
    const rightX = halfWidth + band * slotWidth;
    const leftX = halfWidth - (band + 1) * slotWidth;

    context.fillRect(rightX, y, barWidth, height);
    context.fillRect(leftX, y, barWidth, height);
  });
}

/** The flat baseline frame shared by the "off" and "idle" states. */
function baselineLevels(): number[] {
  return new Array(BAND_COUNT).fill(BASELINE_BAND_LEVEL) as number[];
}

/**
 * Owns the visor's `DynamicTexture` and the material it is painted onto. Constructed once per
 * peer whose head resolved a `Mat.Avatar.Tint` sub-material (`AvatarManager.ts`); a peer whose
 * head has no such slot — the primitive fallback, or a model missing the named slot
 * (`applyAvatarMaterial`'s own fallback) — never gets one, and simply shows no voice
 * visualisation, the same "must never fail to render" degrade `applyAvatarMaterial` itself
 * follows.
 */
// spec: SPEC-109
export class VisorVisualizer {
  private readonly texture: DynamicTexture;

  constructor(
    scene: Scene,
    material: StandardMaterial,
    private readonly presenceColorHex: string,
    name: string,
  ) {
    this.texture = new DynamicTexture(name, { width: TEXTURE_WIDTH, height: TEXTURE_HEIGHT }, scene, false);

    // From here on the texture supplies the material's entire visual appearance. diffuseColor is
    // pinned to white rather than left at whatever colour applyAvatarMaterial's own tint
    // colouring set (AvatarManager.ts): a StandardMaterial multiplies diffuseColor into the
    // sampled texel, and this canvas already paints the peer's real colour itself — a coloured
    // diffuseColor on top would double it up, darker and wrong. disableLighting plus a black
    // specular keep scene lighting from shading it further, the same unlit-plate pattern the
    // avatar's own name plate already uses (Avatar.buildNamePlate).
    material.diffuseTexture = this.texture;
    material.diffuseColor = Color3.White();
    material.emissiveColor = Color3.White();
    material.disableLighting = true;
    material.specularColor = Color3.Black();

    // Draws the resting "no stream" frame immediately, rather than leaving the texture blank
    // until the first 15Hz VoiceBands tick — a freshly-joined avatar should read as "microphone
    // off" from the very first frame it renders, never a flash of emptiness.
    this.draw({ bands: baselineLevels(), rms: 0 }, false);
  }

  /**
   * Redraws the visor for one `VoiceBands` sample. Called on the same 15Hz tick `VoiceBands`
   * itself samples on (`AvatarManager.ts` wires the two together) — never on
   * `requestAnimationFrame`, and never faster than that tick, matching `VoiceBands`' own module
   * doc on why. `hasStream` is the "has this peer's microphone stream even started" half of the
   * three-state split (`PeerVoicePlayer.isActive`, in practice); `sample.rms` decides
   * quiet-vs-speaking within it — see `selectVisorState`.
   */
  draw(sample: VoiceBandsSample, hasStream: boolean): void {
    const state = selectVisorState(hasStream, sample.rms, MIC_QUIET_RMS);
    const context = as2d(this.texture.getContext());

    context.clearRect(0, 0, TEXTURE_WIDTH, TEXTURE_HEIGHT);
    context.fillStyle = this.presenceColorHex;
    context.fillRect(0, 0, TEXTURE_WIDTH, TEXTURE_HEIGHT);

    const levels = state === 'speaking' ? sample.bands : baselineLevels();
    const alpha = state === 'off' ? OFF_BAR_ALPHA : ON_BAR_ALPHA;

    context.fillStyle = `rgba(255, 255, 255, ${alpha})`;
    drawMirroredBars(context, levels);

    this.texture.update();
  }

  /** Frees the texture — never assumed to happen as a side effect of disposing the mesh or
   *  material it is assigned to (CLAUDE.md, "That same cascade does not free materials" — the
   *  same lesson applies one layer further down, to a material's own texture). */
  dispose(): void {
    this.texture.dispose();
  }
}
